/**
 * Listing seed data - full detail projections (CAT-04) in two layers:
 * hand-crafted showcase rows (ids 101-183, byte-stable - orders, auctions and
 * specs reference them) plus a deterministic generator (ids 200+) that stocks
 * every store of every company with realistic depth. Hand-crafted auction rows
 * (110–113, 131–134 and eight fillers) mirror the auction seed exactly;
 * distances are computed client-side, so distanceMi stays null here.
 */
import { ItemCondition, ListingFormat, ListingSource, ListingStatus } from '@core/models/listing.enum';
import { ListingDetailState, ListingShippingState } from '@core/models/listing.state';

import { daysAgo, daysFromNow, hoursAgo, hoursFromNow, minutesFromNow } from '../seed-time';
import { seedCompanies } from '../tenant/company.seed';

const PHOTOS: Record<string, string> = {
  dresser: 'photo-1612908317776-a3afde8232fa',
  mixer: 'photo-1547091267-6b2be403a763',
  lamp: 'photo-1773916398209-84b1a5d95763',
  teapot: 'photo-1765808608323-0d0dbf0f21ef',
  jacket: 'photo-1611312449408-fcece27cdbb7',
  camera: 'photo-1516961642265-531546e84af2',
  records: 'photo-1617618910255-0ff333c8b5d4',
  bike: 'photo-1523740856324-f2ce89135981',
  watch: 'photo-1612817159623-0399784fd0ce',
  chair: 'photo-1489269637500-aa0e75768394',
  books: 'photo-1585521549926-ca6526bda09e',
  mirror: 'photo-1656797590428-653803a957fd',
  skis: 'photo-1578311724368-73da1c2e4faf',
  hmask: 'photo-1772058697410-fd7f11f7007b',
  hskates: 'photo-1580670759122-71ec87c409ad',
  hcards: 'photo-1609200660087-e23d23d7e958',
  hjersey: 'photo-1655089131279-8029e8a21ac6',
  hstick: 'photo-1551241784-8fb63921cf97',
  hmask0: 'photo-1516225668366-88ffccd6f9d1',
  hrecords: 'photo-1607310073276-9f48dec47340',
  hgear: 'photo-1653681498612-37ec55093e29',
  hwatch: 'photo-1578742209557-d10f3f42fdfd',
  hchair: 'photo-1638720495078-facd4bd6625f',
  hmirror: 'photo-1550035213-d042e26b45e0',
  hskis: 'photo-1545471977-94cac22e71ed'
};
const photo = (kind: string, params = ''): string =>
  `https://images.unsplash.com/${PHOTOS[kind]}?auto=format&fit=crop&w=800&q=70${params}`;

/** Denormalized tenant/store fields keyed by storeId - derived from the company seed. */
interface StoreRef {
  tenantId: number;
  tenantName: string;
  tenantSlug: string;
  storeName: string;
  storeCity: string;
  itemNoPrefix: string;
}

const buildStoreRefs = (): Record<number, StoreRef> => {
  const refs: Record<number, StoreRef> = {};
  for (const company of seedCompanies()) {
    for (const store of company.stores) {
      refs[store.id] = {
        tenantId: company.id,
        tenantName: company.name,
        tenantSlug: company.slug,
        storeName: store.name,
        storeCity: store.city,
        itemNoPrefix: store.itemNoPrefix
      };
    }
  }
  return refs;
};

const STORES: Record<number, StoreRef> = buildStoreRefs();

/** Root-to-leaf display path per categoryId (matches category.seed.ts). */
const CATEGORY_PATHS: Record<number, string[]> = {
  1: ['Furniture'],
  2: ['Clothing'],
  3: ['Kitchen & Dining'],
  4: ['Electronics'],
  5: ['Sports'],
  6: ['Books & Media'],
  7: ['Home Decor'],
  8: ['Music & Records'],
  9: ['Toys & Games'],
  10: ['Jewelry & Watches'],
  11: ['Furniture', 'Dressers & Storage'],
  12: ['Furniture', 'Seating'],
  13: ['Furniture', 'Tables'],
  21: ['Clothing', 'Jackets & Coats'],
  22: ['Clothing', 'Denim'],
  41: ['Electronics', 'Cameras'],
  42: ['Electronics', 'Audio'],
  51: ['Sports', 'Hockey'],
  52: ['Sports', 'Cycling'],
  53: ['Sports', 'Winter Sports']
};

const ETAS = [
  'Arrives Aug 18 – Aug 20',
  'Arrives Aug 19 – Aug 21',
  'Arrives Aug 20 – Aug 23',
  'Arrives Aug 22 – Aug 25'
];

/**
 * Shipping-class presets (HLD LST-04, SHP-01): every product carries four
 * shipping numbers - weight (lb) plus boxed length/width/height (in). Staff
 * measure notable items at enrichment; everything else defaults from its
 * shipping class. Future rate quotes consume these numbers.
 */
type ShippingClassKey = 'small-parcel' | 'medium-parcel' | 'large-parcel' | 'bulky' | 'freight';

const SHIPPING_CLASS_DEFAULTS: Record<
  ShippingClassKey,
  { label: string; weightLb: number; lengthIn: number; widthIn: number; heightIn: number }
> = {
  'small-parcel': { label: 'Small parcel', weightLb: 2, lengthIn: 12, widthIn: 9, heightIn: 4 },
  'medium-parcel': { label: 'Medium parcel', weightLb: 8, lengthIn: 18, widthIn: 14, heightIn: 10 },
  'large-parcel': { label: 'Large parcel', weightLb: 20, lengthIn: 26, widthIn: 18, heightIn: 14 },
  bulky: { label: 'Bulky', weightLb: 45, lengthIn: 40, widthIn: 22, heightIn: 18 },
  freight: { label: 'Freight', weightLb: 120, lengthIn: 64, widthIn: 20, heightIn: 33 }
};

/** Default shipping class per category (leaf wins over root; freight flag overrides). */
const CATEGORY_SHIPPING_CLASS: Record<number, ShippingClassKey> = {
  1: 'bulky',
  2: 'small-parcel',
  3: 'medium-parcel',
  4: 'medium-parcel',
  5: 'large-parcel',
  6: 'small-parcel',
  7: 'medium-parcel',
  8: 'medium-parcel',
  9: 'medium-parcel',
  10: 'small-parcel',
  11: 'bulky',
  12: 'bulky',
  13: 'bulky',
  21: 'small-parcel',
  22: 'small-parcel',
  41: 'small-parcel',
  42: 'medium-parcel',
  51: 'medium-parcel',
  52: 'bulky',
  53: 'large-parcel'
};

const slugify = (title: string): string =>
  title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

interface ListingSeed {
  id: number;
  title: string;
  kind: string;
  condition: ItemCondition;
  categoryId: number;
  storeId: number;
  description: string;
  /** Buy Now price; omit for auction rows. */
  price?: number;
  auction?: { currentBid: number; bidCount: number; endsAt: Date; reserveMet?: boolean; buyNowPrice?: number };
  /** Lifecycle override for settled/scheduled showcases; defaults to Live. */
  status?: ListingStatus;
  /** Auction start - set on Scheduled auction listings (AUC-01). */
  startsAt?: Date;
  conditionNote?: string;
  /** Shipping price; omit for pickup-only listings. */
  ship?: number;
  freight?: boolean;
  /** Staff-measured shipping numbers; omit to fall back to the category's shipping-class preset. */
  shipping?: { weightLb: number; lengthIn: number; widthIn: number; heightIn: number };
  honestFlaws?: string;
  retailEstimate?: number;
  photoUrls?: string[];
  dimensionsLabel?: string;
  materials?: string;
  era?: string;
  itemNo?: string;
}

/** Measured numbers win; otherwise the category preset (freight flag promotes to Freight class). */
const buildShipping = (seed: ListingSeed): ListingShippingState => {
  if (seed.shipping) {
    return { ...seed.shipping, isClassDefault: false, shippingClassLabel: null };
  }
  const classKey: ShippingClassKey = seed.freight
    ? 'freight'
    : (CATEGORY_SHIPPING_CLASS[seed.categoryId] ?? 'medium-parcel');
  const preset = SHIPPING_CLASS_DEFAULTS[classKey];
  return {
    weightLb: preset.weightLb,
    lengthIn: preset.lengthIn,
    widthIn: preset.widthIn,
    heightIn: preset.heightIn,
    isClassDefault: true,
    shippingClassLabel: preset.label
  };
};

const make = (seed: ListingSeed): ListingDetailState => {
  const store = STORES[seed.storeId];
  const auction = seed.auction ?? null;
  const shipPrice = seed.ship ?? null;
  const photoUrls = seed.photoUrls ?? [photo(seed.kind)];
  const buyNowPrice = auction?.buyNowPrice ?? null;
  return {
    id: seed.id,
    slug: slugify(seed.title),
    title: seed.title,
    condition: seed.condition,
    conditionNote: seed.conditionNote ?? null,
    format: auction ? ListingFormat.Auction : ListingFormat.BuyNow,
    status: seed.status ?? ListingStatus.Live,
    source: ListingSource.Tpm,
    price: auction ? buyNowPrice : (seed.price ?? null),
    buyNowPrice,
    currentBid: auction ? auction.currentBid : null,
    bidCount: auction ? auction.bidCount : 0,
    startsAt: seed.startsAt ?? null,
    endsAt: auction ? auction.endsAt : null,
    reserveMet: auction?.reserveMet ?? false,
    primaryPhotoUrl: photoUrls[0],
    categoryId: seed.categoryId,
    tenantId: store.tenantId,
    tenantName: store.tenantName,
    tenantSlug: store.tenantSlug,
    storeId: seed.storeId,
    storeName: store.storeName,
    storeCity: store.storeCity,
    pickupAvailable: true,
    pickupOnly: shipPrice === null,
    shippingAvailable: shipPrice !== null,
    shippingPrice: shipPrice,
    distanceMi: null,
    availableQuantity: 1,
    soldCount: 0,
    listedAt: daysAgo(1 + ((seed.id * 7) % 20)),
    description: seed.description,
    honestFlaws: seed.honestFlaws ?? null,
    photoUrls,
    specs: {
      dimensionsLabel: seed.dimensionsLabel ?? null,
      materials: seed.materials ?? null,
      era: seed.era ?? null,
      itemNo: seed.itemNo ?? `${store.itemNoPrefix}-${String((seed.id * 37199) % 1000000).padStart(6, '0')}`
    },
    retailEstimate: seed.retailEstimate ?? null,
    fulfilment: {
      pickupReadyLabel: 'Usually ready in 2 hours',
      pickupHoldDays: 7,
      shippingEtaLabel: shipPrice === null ? null : ETAS[seed.id % ETAS.length],
      shippingMethodLabel:
        shipPrice === null ? null : seed.freight ? 'Freight - curbside delivery' : 'USPS Ground Advantage'
    },
    shipping: buildShipping(seed),
    categoryPath: CATEGORY_PATHS[seed.categoryId]
  };
};

const handcraftedListings = (): ListingDetailState[] => [
  // ── Marketplace must-haves (mockup-verbatim) ──
  make({
    id: 101,
    title: "Vintage Levi's Type III Trucker Jacket - Medium",
    kind: 'jacket',
    price: 48,
    condition: ItemCondition.Excellent,
    categoryId: 22,
    storeId: 79,
    ship: 8.99,
    description:
      'A classic Type III trucker in that perfect medium-wash fade. No rips, no stains - just honest wear ' +
      "where it counts. Tagged a men's medium and fits true."
  }),
  make({
    id: 102,
    title: 'KitchenAid Artisan 5-qt Stand Mixer',
    kind: 'mixer',
    price: 119,
    condition: ItemCondition.Good,
    conditionNote: 'tested',
    categoryId: 3,
    storeId: 79,
    ship: 12.99,
    shipping: { weightLb: 26, lengthIn: 18, widthIn: 12, heightIn: 15 },
    description:
      'An Artisan stand mixer that runs strong and quiet - our kitchen team tested every speed. Comes with ' +
      'the stainless bowl, flat beater, and dough hook.'
  }),
  make({
    id: 103,
    title: 'Eames-Style Molded Lounge Chair',
    kind: 'chair',
    price: 85,
    condition: ItemCondition.Good,
    categoryId: 12,
    storeId: 76,
    description:
      'A molded-shell lounger in the Eames style on a bent-wood base. A couple of light scuffs on the shell, ' +
      'nothing structural. Pickup only - it rides better in a hatchback than a box.'
  }),
  make({
    id: 104,
    title: 'Brass Table Lamp w/ Linen Shade',
    kind: 'lamp',
    price: 34,
    condition: ItemCondition.Excellent,
    categoryId: 7,
    storeId: 79,
    ship: 9.99,
    description:
      'A solid brass base with a warm patina and a clean linen drum shade. Rewired cord, tested and working - ' +
      'plug it in and it glows like it should.'
  }),
  make({
    id: 105,
    title: 'Canon AE-1 35mm Film Camera + 50mm f/1.8',
    kind: 'camera',
    price: 142,
    condition: ItemCondition.Good,
    conditionNote: 'film tested',
    categoryId: 41,
    storeId: 74,
    ship: 11.99,
    description:
      'The legendary AE-1 with the nifty-fifty attached. Meter is accurate and the shutter fires at every ' +
      'speed - we ran a roll through it last week and every frame came back.'
  }),
  make({
    id: 106,
    title: 'Lot of 24 Jazz Vinyl Records',
    kind: 'records',
    price: 65,
    condition: ItemCondition.Good,
    categoryId: 8,
    storeId: 76,
    ship: 14.99,
    description:
      'Twenty-four jazz LPs from a single-owner collection - Blue Note, Verve, and Impulse! pressings in the ' +
      'mix. Sleeves show shelf wear; the vinyl grades VG or better.'
  }),
  make({
    id: 107,
    title: 'Schwinn Cruiser Bike - 26"',
    kind: 'bike',
    price: 78,
    condition: ItemCondition.Fair,
    conditionNote: 'rides well',
    categoryId: 52,
    storeId: 79,
    shipping: { weightLb: 42, lengthIn: 60, widthIn: 32, heightIn: 10 },
    description:
      'A cherry-red Schwinn cruiser with fenders and the original springer seat. Some chrome pitting and paint ' +
      'chips, but she rides smooth. Pickup only.'
  }),
  make({
    id: 108,
    title: 'Vintage Porcelain Teapot - Blue & Gold',
    kind: 'teapot',
    price: 28,
    condition: ItemCondition.NewWithTags,
    categoryId: 3,
    storeId: 21,
    ship: 6.99,
    description:
      "A blue-and-gold porcelain teapot with the maker's sticker still on the base. Not a chip on it - we " +
      'suspect it never once met a stove.'
  }),
  make({
    id: 109,
    title: 'Stack of MCM Design Books (4)',
    kind: 'books',
    price: 18,
    condition: ItemCondition.Good,
    categoryId: 6,
    storeId: 79,
    ship: 5.99,
    description:
      'Four hardcover surveys of mid-century design, heavy on Eames, Saarinen, and Knoll. A great ' +
      'coffee-table stack or a working reference set.'
  }),
  make({
    id: 110,
    title: 'Mid-Century 6-Drawer Lowboy Dresser - Walnut',
    kind: 'dresser',
    auction: { currentBid: 146, bidCount: 12, endsAt: minutesFromNow(102), reserveMet: true, buyNowPrice: 325 },
    condition: ItemCondition.Good,
    conditionNote: 'vintage wear consistent with age',
    categoryId: 11,
    storeId: 79,
    ship: 89,
    freight: true,
    shipping: { weightLb: 96, lengthIn: 64, widthIn: 20, heightIn: 33 },
    retailEstimate: 650,
    photoUrls: [
      photo('dresser'),
      photo('dresser', '&flip=h'),
      photo('dresser', '&crop=focalpoint&fp-x=0.5&fp-y=0.5&fp-z=1.6'),
      photo('dresser', '&crop=focalpoint&fp-x=0.5&fp-y=0.25&fp-z=1.8')
    ],
    description:
      'A walnut-veneer mid-century lowboy dresser, c. 1960s, with six dovetailed drawers and the original ' +
      'sculpted pulls. Drawers glide smoothly; solid and sturdy with no wobble. Freshly cleaned and oiled by ' +
      'our furniture team. A statement piece for a bedroom or an entryway - this style rarely lasts a day on ' +
      'our floor.',
    honestFlaws:
      'Light ring mark on the top surface (photo 4) and minor veneer wear on the left rear corner. ' +
      'Priced accordingly.',
    dimensionsLabel: '62″ W × 18″ D × 31″ H',
    materials: 'Walnut veneer, solid wood frame',
    era: '1960s · Mid-Century Modern',
    itemNo: 'GNM-SNM-048291'
  }),
  make({
    id: 111,
    title: 'Seiko 5 Automatic Watch - 1978',
    kind: 'watch',
    auction: { currentBid: 96, bidCount: 8, endsAt: minutesFromNow(190), reserveMet: false },
    condition: ItemCondition.Good,
    categoryId: 10,
    storeId: 76,
    ship: 8.99,
    description:
      'A 1978 Seiko 5 automatic with the day-date at three. Keeps time within a minute a day on the wrist. ' +
      'Original bracelet with honest desk-diver wear.'
  }),
  make({
    id: 112,
    title: 'Gilt Ornate Wall Mirror - 34″',
    kind: 'mirror',
    auction: { currentBid: 61, bidCount: 5, endsAt: minutesFromNow(303), reserveMet: true },
    condition: ItemCondition.Good,
    categoryId: 7,
    storeId: 21,
    ship: 24.99,
    shipping: { weightLb: 18, lengthIn: 38, widthIn: 38, heightIn: 6 },
    description:
      'An ornate gilt-framed wall mirror, 34 inches across. The glass is original with light foxing at the ' +
      'edges that reads as charm, not damage.'
  }),
  make({
    id: 113,
    title: 'Vintage Rossignol Skis + Poles',
    kind: 'skis',
    auction: { currentBid: 22, bidCount: 2, endsAt: hoursFromNow(28), reserveMet: false },
    condition: ItemCondition.Good,
    categoryId: 53,
    storeId: 71,
    ship: 29.99,
    shipping: { weightLb: 14, lengthIn: 74, widthIn: 10, heightIn: 6 },
    description:
      'Vintage Rossignol skis with matching poles - straight out of a 1970s lodge photo. Perfect for cabin ' +
      'décor or a retro rock-ski day.'
  }),
  // ── Matt's Finds buy-now (storefront) ──
  make({
    id: 121,
    title: 'Vintage Pro Jerseys - Rack Picks',
    kind: 'hjersey',
    price: 65,
    condition: ItemCondition.Excellent,
    categoryId: 51,
    storeId: 41,
    ship: 9.99,
    description:
      'Rack picks from our vintage jersey wall - pro and minor-league sweaters in genuinely wearable shape. ' +
      "Tell us your size at pickup and we'll pull the best match."
  }),
  make({
    id: 122,
    title: 'Bauer Supreme Hockey Skates - Size 10',
    kind: 'hskates',
    price: 89,
    condition: ItemCondition.Good,
    conditionNote: 'blades sharpened',
    categoryId: 51,
    storeId: 41,
    ship: 12.99,
    description:
      "Bauer Supreme skates in a men's size 10, freshly sharpened on our in-house radius. The boots still " +
      'have plenty of stiffness left in them.'
  }),
  make({
    id: 123,
    title: 'Vintage White Figure Skates - Size 8',
    kind: 'hchair',
    price: 38,
    condition: ItemCondition.Good,
    categoryId: 51,
    storeId: 42,
    ship: 11.99,
    description:
      'Classic white leather figure skates, size 8, with clean blades and brand-new laces. Ready for the pond ' +
      'or the mantel - no judgment either way.'
  }),
  make({
    id: 124,
    title: 'Pro Composite Stick + Game Puck',
    kind: 'hstick',
    price: 45,
    condition: ItemCondition.Excellent,
    categoryId: 51,
    storeId: 41,
    ship: 14.99,
    description:
      'A pro composite stick paired with a game puck from the same night. Low kick point, factory curve, and ' +
      'no cracks - we checked it under the shop light.'
  }),
  make({
    id: 125,
    title: 'Vintage Trading Card Lot - 140+ (70s–80s)',
    kind: 'hcards',
    price: 120,
    condition: ItemCondition.Good,
    conditionNote: 'sleeved',
    categoryId: 51,
    storeId: 43,
    ship: 5.99,
    description:
      "A 140-plus card lot spanning the '70s and '80s, every card sleeved. Stars, semi-stars, and the odd " +
      'goalie gem - sorted by team, ready to hunt through.'
  }),
  make({
    id: 126,
    title: 'Unsorted Card Collection - Binder + Loose',
    kind: 'hrecords',
    price: 65,
    condition: ItemCondition.Good,
    categoryId: 51,
    storeId: 42,
    ship: 8.99,
    description:
      'One full binder plus a shoebox of loose cards, straight from a house cleanout. We have not sorted it - ' +
      "that's the fun part, and the gamble."
  }),
  make({
    id: 127,
    title: 'Full Youth Gear Bag - Complete Set',
    kind: 'hgear',
    price: 78,
    condition: ItemCondition.Fair,
    conditionNote: 'all pieces',
    categoryId: 51,
    storeId: 41,
    description:
      'A complete youth gear bag: pads, gloves, pants, shin guards, and helmet. All pieces present and sized ' +
      'roughly for ages 8 to 10. Pickup only.'
  }),
  make({
    id: 128,
    title: 'Pro-Stock Goalie Mask - Repainted',
    kind: 'hmask0',
    price: 140,
    condition: ItemCondition.Excellent,
    categoryId: 51,
    storeId: 43,
    ship: 12.99,
    description:
      'A pro-stock goalie mask with a clean custom repaint. Certified shell and fresh interior padding - ' +
      'ready for the crease or the display shelf.'
  }),
  // ── Matt's Finds auctions ──
  make({
    id: 131,
    title: 'Vintage 1970s Goalie Mask & Pads Set',
    kind: 'hmask',
    auction: { currentBid: 146, bidCount: 12, endsAt: minutesFromNow(105), reserveMet: true },
    condition: ItemCondition.Good,
    categoryId: 51,
    storeId: 41,
    ship: 24.99,
    description:
      'A full 1970s goalie set - fiberglass mask and leather pads, all original. Museum-grade survivors from ' +
      "a local collector's basement."
  }),
  make({
    id: 132,
    title: 'Game-Worn Practice Jersey - Signed',
    kind: 'hwatch',
    auction: { currentBid: 96, bidCount: 8, endsAt: minutesFromNow(195), reserveMet: false },
    condition: ItemCondition.Good,
    categoryId: 51,
    storeId: 42,
    ship: 8.99,
    description:
      'A practice-worn jersey with a verified signature across the back numbers. The wear marks are real - ' +
      'this one earned them.'
  }),
  make({
    id: 133,
    title: 'Vintage Leather Skates - Restored',
    kind: 'hmirror',
    auction: { currentBid: 61, bidCount: 5, endsAt: minutesFromNow(300), reserveMet: true },
    condition: ItemCondition.Good,
    categoryId: 51,
    storeId: 44,
    ship: 11.99,
    description:
      'Tube skates in vintage leather, restored by our shop: new laces, conditioned leather, and polished ' +
      'blades. They look better than they have in fifty years.'
  }),
  make({
    id: 134,
    title: 'Game-Used Puck & Photo Lot',
    kind: 'hskis',
    auction: { currentBid: 22, bidCount: 2, endsAt: hoursFromNow(29), reserveMet: false },
    condition: ItemCondition.Good,
    categoryId: 51,
    storeId: 45,
    ship: 6.99,
    description:
      'A game-used puck paired with an original press photo from the same season. Comes with a small display ' +
      'stand so it can sit on the desk where it belongs.'
  }),
  // ── Marketplace fillers ──
  make({
    id: 141,
    title: 'KitchenAid Artisan Stand Mixer - Serviced',
    kind: 'mixer',
    price: 85,
    condition: ItemCondition.Excellent,
    conditionNote: 'tested',
    categoryId: 3,
    storeId: 78,
    ship: 12.99,
    photoUrls: [photo('mixer', '&crop=focalpoint&fp-z=1.3')],
    description:
      'An Artisan tilt-head mixer our kitchen bench just serviced: fresh grease in the gearbox and every ' +
      'speed tested under load. Bowl and flat beater included.'
  }),
  make({
    id: 142,
    title: "Lee Rider Denim Jacket - 1980s, Men's M",
    kind: 'jacket',
    price: 64,
    condition: ItemCondition.Excellent,
    categoryId: 22,
    storeId: 77,
    ship: 9.99,
    photoUrls: [photo('jacket', '&sat=-20')],
    description:
      "A 1980s Lee Rider with the zigzag stitch and original buttons. Even color, tight cuffs, and a men's " +
      'medium that fits true.'
  }),
  make({
    id: 143,
    title: 'Sony Walkman WM-FX290',
    kind: 'camera',
    price: 35,
    condition: ItemCondition.Good,
    conditionNote: 'tested',
    categoryId: 42,
    storeId: 74,
    ship: 5.99,
    description:
      'A Sony Walkman with auto-reverse and an FM tuner. Fresh belt, plays tapes without warble - we ran a ' +
      'full album through it to be sure.'
  }),
  make({
    id: 144,
    title: 'Fisher-Price Little People Farm - Complete',
    kind: 'books',
    price: 24,
    condition: ItemCondition.Good,
    categoryId: 9,
    storeId: 76,
    ship: 8.99,
    description:
      'The classic barn with the mooing door still mooing. Complete set: farmer, animals, silo, and fencing ' +
      'all accounted for.'
  }),
  make({
    id: 145,
    title: 'Solid Oak Farmhouse Dining Table',
    kind: 'chair',
    price: 180,
    condition: ItemCondition.Good,
    categoryId: 13,
    storeId: 21,
    description:
      'A solid oak farmhouse table that seats six with elbow room. Sturdy joints and no wobble - just honest ' +
      'character in the top. Pickup only.'
  }),
  make({
    id: 146,
    title: 'Lee Storm Rider Denim Jacket - Blanket Lined, Large',
    kind: 'jacket',
    price: 95,
    condition: ItemCondition.Good,
    categoryId: 21,
    storeId: 78,
    ship: 9.99,
    photoUrls: [photo('jacket', '&flip=h')],
    description:
      'The classic Storm Rider: denim shell, wool blanket lining, and the tan corduroy collar. Heavyweight ' +
      'and honest - broken in, not beat up.'
  }),
  make({
    id: 147,
    title: 'Technics SL-B210 Turntable',
    kind: 'records',
    price: 110,
    condition: ItemCondition.Good,
    conditionNote: 'new belt',
    categoryId: 42,
    storeId: 71,
    ship: 14.99,
    description:
      'A Technics belt-drive turntable with a fresh belt and a clean stylus. Tracked a test record start to ' +
      'finish without a skip.'
  }),
  make({
    id: 148,
    title: 'Mid-Century Teak Nesting Tables - Set of 3',
    kind: 'chair',
    price: 145,
    condition: ItemCondition.Excellent,
    categoryId: 13,
    storeId: 22,
    description:
      'A set of three teak nesting tables with lovely grain and clean joinery. They slide apart perfectly and ' +
      'there is not a ring on any top. Pickup only.'
  }),
  make({
    id: 149,
    title: 'Herman Miller Aeron Chair - Size B',
    kind: 'chair',
    auction: { currentBid: 132, bidCount: 9, endsAt: minutesFromNow(45), reserveMet: true },
    condition: ItemCondition.Good,
    categoryId: 12,
    storeId: 73,
    description:
      'A size-B Aeron with fully working tilt and lumbar. The mesh is intact on both seat and back - these ' +
      'never make it to the sales floor. Pickup only.'
  }),
  make({
    id: 150,
    title: 'Texas Instruments TI-84 Plus',
    kind: 'camera',
    price: 40,
    condition: ItemCondition.Good,
    conditionNote: 'tested',
    categoryId: 4,
    storeId: 74,
    ship: 5.99,
    description:
      'A TI-84 Plus that has clearly survived finals week more than once. The screen is crisp, every key ' +
      'registers, and the slide cover is included.'
  }),
  make({
    id: 151,
    title: 'Stainless Mixer Bowl & Beater Set - Fits 5-qt Tilt-Head',
    kind: 'mixer',
    price: 42,
    condition: ItemCondition.Good,
    conditionNote: 'complete set',
    categoryId: 3,
    storeId: 77,
    ship: 11.99,
    photoUrls: [photo('mixer', '&crop=focalpoint&fp-x=0.45&fp-y=0.7&fp-z=1.6')],
    description:
      'A stainless 5-quart bowl with the matching flat beater and dough hook, shown seated on our shop mixer. ' +
      'No dents or rust - a cheap way to bring a bare machine back to work.'
  }),
  make({
    id: 152,
    title: "Levi's Denim Jacket - Broken In, Men's S",
    kind: 'jacket',
    price: 38,
    condition: ItemCondition.Good,
    categoryId: 22,
    storeId: 76,
    ship: 6.99,
    photoUrls: [photo('jacket', '&crop=focalpoint&fp-y=0.3&fp-z=1.2')],
    description:
      "A broken-in Levi's denim jacket in a men's small. Even fade across the chest, no rips, and both " +
      'pockets still hold their shape.'
  }),
  make({
    id: 153,
    title: 'Art Deco Waterfall Dresser - 1930s',
    kind: 'dresser',
    auction: { currentBid: 84, bidCount: 6, endsAt: hoursFromNow(9), reserveMet: false },
    condition: ItemCondition.Good,
    categoryId: 11,
    storeId: 22,
    ship: 79,
    freight: true,
    description:
      'A 1930s waterfall dresser with the rounded front and original bakelite pulls. The drawers run smooth ' +
      'after a wax rub from our furniture team.'
  }),
  make({
    id: 154,
    title: 'Nikon FE 35mm Body',
    kind: 'camera',
    price: 98,
    condition: ItemCondition.Good,
    conditionNote: 'meter works',
    categoryId: 41,
    storeId: 78,
    ship: 9.99,
    description:
      'A Nikon FE body in clean working order - the meter reads accurate against our reference. Bring your ' +
      'favorite lens and go shoot.'
  }),
  make({
    id: 155,
    title: 'Sony CFS-B15 Boombox - Works',
    kind: 'records',
    price: 55,
    condition: ItemCondition.Fair,
    conditionNote: 'plays tapes',
    categoryId: 42,
    storeId: 77,
    ship: 16.99,
    description:
      "An '80s Sony boombox that still does it all: tape, radio, and that satisfying clunk of the play " +
      'button. Some scuffs, all function.'
  }),
  make({
    id: 156,
    title: "Chicago Rink Roller Skates - Women's 9",
    kind: 'hskates',
    price: 32,
    condition: ItemCondition.Good,
    categoryId: 5,
    storeId: 79,
    ship: 10.99,
    description:
      "Classic Chicago rink skates in women's 9 with clean wheels and smooth bearings. The laces are new; " +
      'the nostalgia is original.'
  }),
  make({
    id: 157,
    title: 'KitchenAid Classic Stand Mixer - Well Loved',
    kind: 'mixer',
    price: 48,
    condition: ItemCondition.Good,
    conditionNote: 'runs strong',
    categoryId: 3,
    storeId: 71,
    ship: 13.99,
    photoUrls: [photo('mixer', '&sat=-30')],
    description:
      'A Classic-series tilt-head mixer with a decade of bread dough behind it and plenty left in the motor. ' +
      'Cosmetic scuffs on the band; every speed engages cleanly.'
  }),
  make({
    id: 158,
    title: 'Signed First Editions - PNW Author Lot',
    kind: 'books',
    auction: { currentBid: 41, bidCount: 4, endsAt: hoursFromNow(52), reserveMet: false },
    condition: ItemCondition.Excellent,
    categoryId: 6,
    storeId: 78,
    ship: 7.99,
    description:
      'Four signed first editions from Pacific Northwest authors, dust jackets protected in mylar. ' +
      'Signatures checked against our references.'
  }),
  make({
    id: 159,
    title: 'Samsonite Hardside Luggage Set - 3 pc',
    kind: 'lamp',
    price: 44,
    condition: ItemCondition.Fair,
    categoryId: 7,
    storeId: 74,
    description:
      'A three-piece Samsonite hardside set in that perfect 1970s marbled blue. The latches snap tight and ' +
      'the interiors are clean. Pickup only.'
  }),
  make({
    id: 160,
    title: 'Gilt Floral Porcelain Teapot - Turquoise',
    kind: 'teapot',
    price: 38,
    condition: ItemCondition.Excellent,
    categoryId: 3,
    storeId: 79,
    ship: 9.99,
    photoUrls: [photo('teapot', '&crop=focalpoint&fp-y=0.4&fp-z=1.3')],
    description:
      'A turquoise porcelain teapot with hand-painted gilt florals and a bright gold spout. The pattern is ' +
      'unworn and the lid seats with a satisfying click.'
  }),
  make({
    id: 161,
    title: "Denim Chore Coat - French Workwear Style, Women's M",
    kind: 'jacket',
    price: 58,
    condition: ItemCondition.Excellent,
    categoryId: 21,
    storeId: 76,
    ship: 7.99,
    photoUrls: [photo('jacket', '&flip=h&sat=-25')],
    description:
      'A French-workwear-style denim chore coat with patch pockets and clean seams. Barely worn - the ' +
      'buttons still have their shine and the cuffs are crisp.'
  }),
  make({
    id: 162,
    title: 'Vintage Brass Candlestick Pair',
    kind: 'lamp',
    price: 22,
    condition: ItemCondition.Good,
    categoryId: 7,
    storeId: 21,
    ship: 6.99,
    description:
      'A pair of weighted brass candlesticks with a soft, even patina. Polish them bright or leave them ' +
      'moody - both are correct.'
  }),
  make({
    id: 163,
    title: 'Squier Stratocaster - Sunburst',
    kind: 'records',
    auction: { currentBid: 156, bidCount: 11, endsAt: daysFromNow(1), reserveMet: true },
    condition: ItemCondition.Good,
    categoryId: 8,
    storeId: 73,
    ship: 24.99,
    description:
      'A Squier Stratocaster in three-tone sunburst with a straight neck and low action. New strings and a ' +
      'full setup done in-house.'
  }),
  make({
    id: 164,
    title: 'Monopoly 1961 Edition - Complete',
    kind: 'books',
    price: 18,
    condition: ItemCondition.Good,
    conditionNote: 'all pieces',
    categoryId: 9,
    storeId: 21,
    ship: 8.99,
    description:
      'A 1961 Monopoly with every deed, house, and token present - we counted twice. The box shows shelf ' +
      'wear; the money is crisp.'
  }),
  make({
    id: 165,
    title: 'Citizen Eco-Drive Chronograph',
    kind: 'watch',
    price: 88,
    condition: ItemCondition.Excellent,
    categoryId: 10,
    storeId: 74,
    ship: 8.99,
    description:
      'A Citizen Eco-Drive chronograph that charges off any light and keeps perfect time. The sapphire ' +
      'crystal is unmarked.'
  }),
  make({
    id: 166,
    title: "Wrangler Lined Denim Chore Jacket - Men's L",
    kind: 'jacket',
    price: 72,
    condition: ItemCondition.Good,
    categoryId: 21,
    storeId: 71,
    ship: 9.99,
    photoUrls: [photo('jacket', '&crop=focalpoint&fp-y=0.45&fp-z=1.35')],
    description:
      'A lined Wrangler chore jacket with a corduroy collar and a warm blanket body. Even fade, tight seams, ' +
      'and snaps that still snap with authority.'
  }),
  make({
    id: 167,
    title: 'Mid-Century Walnut Bookshelf',
    kind: 'dresser',
    price: 95,
    condition: ItemCondition.Good,
    categoryId: 1,
    storeId: 22,
    ship: 69,
    freight: true,
    description:
      'A mid-century walnut bookshelf with three fixed shelves and lovely grain. Solid wood, no veneer ' +
      'chips, and it sits dead flat.'
  }),
  make({
    id: 168,
    title: 'Trek 520 Touring Bike - 1987',
    kind: 'bike',
    auction: { currentBid: 178, bidCount: 14, endsAt: daysFromNow(2), reserveMet: true },
    condition: ItemCondition.Good,
    categoryId: 52,
    storeId: 78,
    description:
      'A 1987 Trek 520 tourer, lugged steel in metallic blue. Recently tuned: new cables, fresh bar tape, ' +
      'hubs repacked. Pickup only.'
  }),
  make({
    id: 169,
    title: 'Chinoiserie Teapot on Warmer Stand',
    kind: 'teapot',
    price: 48,
    condition: ItemCondition.Excellent,
    categoryId: 7,
    storeId: 77,
    ship: 14.99,
    photoUrls: [photo('teapot', '&flip=h')],
    description:
      'A chinoiserie porcelain teapot resting on its matching warmer stand, hand-painted in cobalt and gold. ' +
      'Not a chip on either piece - clearly a display darling, never a workhorse.'
  }),
  make({
    id: 170,
    title: "Selvedge Denim Jacket - Japanese Repro, Men's L",
    kind: 'jacket',
    price: 85,
    condition: ItemCondition.Good,
    categoryId: 22,
    storeId: 79,
    ship: 7.99,
    photoUrls: [photo('jacket', '&flip=h&crop=focalpoint&fp-y=0.4&fp-z=1.3')],
    description:
      'A Japanese-made selvedge repro of the classic type-two jacket, broken in just enough to show the ' +
      'high-contrast fade these are bought for. Chain-stitched hems intact.'
  }),
  make({
    id: 171,
    title: 'Yamaha FG-75 Acoustic Guitar',
    kind: 'records',
    price: 120,
    condition: ItemCondition.Good,
    conditionNote: 'new strings',
    categoryId: 8,
    storeId: 74,
    description:
      'A Yamaha FG-75 with that warm plywood-era tone the internet loves. New strings, straight neck, action ' +
      'set medium-low. Pickup only.'
  }),
  make({
    id: 172,
    title: 'Royal Quiet De Luxe Typewriter - 1952',
    kind: 'camera',
    auction: { currentBid: 66, bidCount: 7, endsAt: daysFromNow(3), reserveMet: false },
    condition: ItemCondition.Good,
    categoryId: 7,
    storeId: 21,
    ship: 19.99,
    description:
      'A 1952 Royal Quiet De Luxe in gray crinkle finish, fully working with a fresh ribbon. Types a clean, ' +
      'even line across the page.'
  }),
  make({
    id: 173,
    title: 'Head Monster 78 Skis + Bindings',
    kind: 'skis',
    price: 88,
    condition: ItemCondition.Good,
    categoryId: 53,
    storeId: 73,
    description:
      'Head Monster 78s with Tyrolia bindings, edges sharp and bases freshly waxed. A dependable ' +
      'all-mountain daily driver. Pickup only.'
  }),
  make({
    id: 174,
    title: 'American Girl Doll - Samantha + Outfits',
    kind: 'books',
    price: 110,
    condition: ItemCondition.Excellent,
    categoryId: 9,
    storeId: 78,
    ship: 12.99,
    description:
      'Samantha in excellent shape with her original meet dress plus three extra outfits. Her hair has been ' +
      'lovingly detangled by a volunteer who cared.'
  }),
  make({
    id: 175,
    title: 'Fossil Watch Lot - 3 Pieces, Running',
    kind: 'watch',
    price: 46,
    condition: ItemCondition.Good,
    conditionNote: 'new batteries',
    categoryId: 10,
    storeId: 77,
    ship: 6.99,
    description:
      'Three Fossil watches, all running on fresh batteries. A leather strap, a mesh, and a chrono - one for ' +
      'each mood.'
  }),
  make({
    id: 176,
    title: "Collector's Porcelain Teapot - Hand-Painted Gilt",
    kind: 'teapot',
    auction: { currentBid: 34, bidCount: 3, endsAt: daysFromNow(4), reserveMet: false, buyNowPrice: 60 },
    condition: ItemCondition.Good,
    categoryId: 3,
    storeId: 76,
    ship: 15.99,
    photoUrls: [photo('teapot', '&sat=-15&crop=focalpoint&fp-z=1.15')],
    description:
      'A hand-painted porcelain teapot with gilt trim from a single-owner cabinet collection. One tiny glaze ' +
      'fleck under the spout; the lid ring is clean.'
  }),
  make({
    id: 177,
    title: 'Restoration Hardware Leather Club Chair',
    kind: 'chair',
    auction: { currentBid: 190, bidCount: 16, endsAt: daysFromNow(6), reserveMet: true, buyNowPrice: 350 },
    condition: ItemCondition.Excellent,
    categoryId: 12,
    storeId: 22,
    ship: 99,
    freight: true,
    description:
      'A Restoration Hardware leather club chair with years of good patina left in it. The cushions are firm ' +
      'and the leather glows.'
  }),
  make({
    id: 178,
    title: 'K2 Snowboard w/ Bindings - 155 cm',
    kind: 'skis',
    price: 95,
    condition: ItemCondition.Good,
    categoryId: 53,
    storeId: 78,
    description:
      'A K2 155 with bindings mounted and edges tuned. The base has a few filled scratches - character, not ' +
      'damage. Pickup only.'
  }),
  make({
    id: 179,
    title: 'Ray-Ban Wayfarer - Tortoise',
    kind: 'watch',
    price: 54,
    condition: ItemCondition.NewWithTags,
    categoryId: 2,
    storeId: 77,
    ship: 5.99,
    description:
      'Tortoise Wayfarers, new with tags and the original case. Classic G-15 lenses with no scratches - ' +
      'never worn past the store mirror.'
  }),
  make({
    id: 180,
    title: 'KitchenAid Ultra Power Stand Mixer - Pink',
    kind: 'mixer',
    price: 95,
    condition: ItemCondition.Excellent,
    conditionNote: 'tested',
    categoryId: 3,
    storeId: 73,
    ship: 12.99,
    photoUrls: [photo('mixer', '&flip=h')],
    description:
      'An Ultra Power stand mixer in that unmissable pink, tested through every speed with the whisk on. ' +
      'Runs quiet, sits steady, and the finish still shines.'
  }),
  // ── Auction lifecycle showcases (mirror auctions 1017-1019) ──
  // Sold: won by the mock shopper - PDP won state + My Bids "Won" (AUC-07).
  make({
    id: 181,
    title: 'Herman Miller Aeron Chair - Size B',
    kind: 'hchair',
    condition: ItemCondition.Good,
    conditionNote: 'fully adjustable',
    categoryId: 12,
    storeId: 79,
    status: ListingStatus.Sold,
    auction: { currentBid: 410, bidCount: 18, endsAt: hoursAgo(26), reserveMet: true },
    shipping: { weightLb: 43, lengthIn: 27, widthIn: 27, heightIn: 41 },
    retailEstimate: 1200,
    description:
      'A Size B Aeron with the PostureFit back and fully working tilt and lumbar adjustments. The mesh is ' +
      'clean and taut - our furniture team sat in it for a week and fought over who kept it.'
  }),
  // Sold: won by an anonymous bidder - Jess's proxy was outbid (My Bids "Lost").
  make({
    id: 182,
    title: 'Seiko SKX007 Automatic Dive Watch',
    kind: 'watch',
    condition: ItemCondition.Good,
    conditionNote: 'keeps time',
    categoryId: 10,
    storeId: 76,
    ship: 8.99,
    status: ListingStatus.Sold,
    auction: { currentBid: 132, bidCount: 11, endsAt: daysAgo(2) },
    retailEstimate: 300,
    description:
      'The cult-classic SKX007 on its original jubilee bracelet. Winds, sets, and keeps time within a few ' +
      'seconds a day. Desk-diver wear on the clasp, crystal is clean.'
  }),
  // Scheduled: auction goes live in 6 hours (AUC-01) - hidden from search,
  // the PDP shows a starts-in countdown with bidding disabled.
  make({
    id: 183,
    title: 'Vintage Full-Length Brass Mirror',
    kind: 'mirror',
    condition: ItemCondition.Excellent,
    categoryId: 7,
    storeId: 21,
    status: ListingStatus.Scheduled,
    startsAt: hoursFromNow(6),
    auction: { currentBid: 60, bidCount: 0, endsAt: hoursFromNow(78) },
    shipping: { weightLb: 31, lengthIn: 68, widthIn: 24, heightIn: 6 },
    retailEstimate: 240,
    description:
      'A full-length brass-framed mirror with a soft antique patina and clean original glass. Leans ' +
      'beautifully or hangs from the built-in brackets. Auction opens tonight.'
  })
];

/* ── Deterministic catalog generator (ids 200+) ──────────────────────────────
 *
 * Expands the hand-crafted catalog above with ~230 generated listings so every
 * store of every company shows real inventory depth (facets, pagination and
 * derived tenant stats all read from actual rows). Fully DETERMINISTIC: a
 * seeded mulberry32 PRNG with a fixed literal seed - Math.random is banned in
 * seeds - so every run produces the identical catalog. `auction.seed.ts`
 * derives matching auction + bid rows from the same specs.
 */

/** Fixed generator seed - a literal, never derived from the clock. */
const GENERATOR_SEED = 0x5eedf1d5;

/** mulberry32 - tiny 32-bit deterministic PRNG returning draws in [0, 1). */
const mulberry32 = (seed: number): (() => number) => {
  let state = seed;
  return () => {
    state = (state + 0x6d2b79f5) | 0;
    let t = Math.imul(state ^ (state >>> 15), state | 1);
    t = (t + Math.imul(t ^ (t >>> 7), t | 61)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const pick = <T>(rng: () => number, values: readonly T[]): T => values[Math.floor(rng() * values.length)];

/**
 * Distinct crop/flip/saturation/brightness params per reuse of a photo id -
 * the cycle lengths are coprime, so no two generated cards render the same
 * frame of the same photo.
 */
const photoVariant = (use: number): string => {
  const flip = use % 2 === 1 ? '&flip=h' : '';
  const sat = -(6 + ((use * 5) % 27));
  const bri = ((use * 3) % 13) - 6;
  const fpx = (0.35 + ((use * 11) % 31) / 100).toFixed(2);
  const fpy = (0.3 + ((use * 13) % 26) / 100).toFixed(2);
  const fpz = (1.05 + ((use * 7) % 55) / 100).toFixed(2);
  return `${flip}&sat=${sat}&bri=${bri}&crop=focalpoint&fp-x=${fpx}&fp-y=${fpy}&fp-z=${fpz}`;
};

interface CategoryPool {
  /** Photo kinds drawn from the shared Unsplash pool above. */
  kinds: string[];
  /** Buy Now price band; auction economics derive from the same band. */
  band: [number, number];
  titles: string[];
  /** Category-specific middle sentences for the staff-voice description. */
  details: string[];
}

const OPENERS = [
  'Came in on a recent donation run and went straight to the front of the floor.',
  'A single-owner piece that arrived clean and ready to price.',
  'Fresh off the intake bench this week.',
  'One of those finds our floor team argues over.',
  'Spotted at intake by our pricing team and pulled aside immediately.',
  'A donation-day standout that survived the staff hold shelf.'
];

const CLOSERS = [
  'Priced to move - pieces like this rarely sit long on our floor.',
  'Free in-store pickup, usually ready within a couple of hours.',
  'Photographed exactly as found - what you see is what you get.',
  'Condition graded honestly by store staff, flaws and all.',
  'Ask at the counter if you want extra photos before you commit.',
  'Our regulars would tell you to grab this one before they do.'
];

const FAIR_NOTES = ['honest wear', 'works great', 'priced accordingly', 'solid bones'];
const GOOD_NOTES = ['tested', 'complete', 'cleaned', 'serviced'];

const CATEGORY_POOLS: Record<number, CategoryPool> = {
  1: {
    kinds: ['dresser', 'chair'],
    band: [45, 220],
    titles: [
      'Solid Maple Bookcase - 5 Shelf',
      'Broyhill Cedar Chest',
      'Rattan Peacock Chair',
      'Drexel Campaign Desk',
      'Ethan Allen Nightstand',
      'Oak Mission-Style Bench',
      'Pine Blanket Chest',
      'Walnut Record Cabinet',
      'Danish Teak Sideboard',
      'Windsor-Back Dining Chairs - Set of 4',
      'Oak Roll-Top Desk',
      'Cane-Back Armchair',
      'Cedar Wardrobe - Double Door'
    ],
    details: [
      'Joints are tight and it sits dead flat - our furniture bench checked every corner.',
      'Solid wood through and through, with the kind of grain veneer never gets right.',
      'Wiped down, waxed, and ready to live another few decades.',
      'A few shelf-life scuffs in the finish, nothing a coaster and low light will not forgive.'
    ]
  },
  2: {
    kinds: ['jacket'],
    band: [12, 68],
    titles: [
      "Pendleton Wool Shirt - Men's L",
      "Patagonia Fleece Pullover - Women's M",
      'Carhartt Duck Vest - Large',
      "London Fog Trench Coat - Men's 42R",
      'Silk Scarf Lot - 6 Pieces',
      'Woolrich Buffalo Plaid Shirt - XL',
      "Eddie Bauer Down Vest - Men's M",
      'Leather Belt Lot - 4 Sizes',
      'Harris Tweed Blazer - 42R',
      "Cashmere Cardigan - Women's S",
      'Wool Peacoat - Navy, Medium',
      'Leather Work Boots - Size 9',
      'Silk Blouse Lot - 5 Pieces',
      'Corduroy Shirt Jacket - Medium'
    ],
    details: [
      'No stains, no pulls - it went straight from the sorting table to a hanger.',
      'Checked seam by seam under the rack lights; buttons and zips all do their job.',
      'The tag matches the fit, which is rarer than it should be with vintage sizing.',
      'Laundered and steamed by our textiles volunteer before it hit the floor.'
    ]
  },
  3: {
    kinds: ['mixer', 'teapot'],
    band: [14, 120],
    titles: [
      'Le Creuset Dutch Oven - 5.5 qt Flame',
      'Pyrex Primary Colors Bowl Set - Complete',
      'Lodge Cast Iron Skillet - No. 8',
      'Corningware Blue Cornflower Set - 6 pc',
      'Cuisinart Food Processor - Serviced',
      'Fiestaware Dinner Set - 12 pc',
      'Copper Stock Pot - Hammered',
      'Descoware Enameled Casserole',
      'Osterizer Beehive Blender - Chrome',
      'Mikasa China Set - Service for 8',
      'Revere Ware Copper-Bottom Set - 7 pc',
      'Stoneware Mixing Bowl Trio',
      'Vintage Tupperware Canister Set',
      'Cast Iron Griddle - Rectangular',
      'Wedgwood Teacup Set - Service for 6',
      'Enamel Coffee Percolator'
    ],
    details: [
      'No chips or crazing - we run a fingertip around every rim before pricing.',
      'The kitchen bench tested it against a weeknight dinner and it passed.',
      'Seasoned, scrubbed, and ready to go straight onto your burner.',
      'From an estate kitchen where things were clearly loved and looked after.'
    ]
  },
  4: {
    kinds: ['camera'],
    band: [15, 90],
    titles: [
      'Panasonic Cassette Deck - Serviced',
      'Atari 2600 Console + 6 Games',
      'JVC Camcorder - Full Kit in Case',
      'Vintage Rotary Phone - Bell Green',
      'Casio Keyboard - 61 Key',
      'Kindle Paperwhite - 10th Gen',
      'Polaroid OneStep - Film Tested'
    ],
    details: [
      'Powered on and put through its paces at the electronics bench.',
      'All the buttons, switches and dials respond the way the manual promises.',
      'Comes with the cables you actually need, tucked in the box.',
      'A little cosmetic wear on the shell; the internals behave like new.'
    ]
  },
  5: {
    kinds: ['bike', 'hskates', 'skis'],
    band: [18, 95],
    titles: [
      'Wilson Tennis Racquet - Restrung',
      'Coleman Camp Stove - 2 Burner',
      'Golf Club Set + Bag - Right Handed',
      'Spalding Leather Basketball',
      'Fishing Rod & Reel Combo',
      'Weight Bench + 90 lb Plates',
      'Yoga & Balance Bundle - Complete Set',
      'Bocce Ball Set - Full Case',
      'Kayak Paddle Pair - Adjustable',
      'Camping Lantern Lot - 3 Tested'
    ],
    details: [
      'Checked over in the back room - everything tightens, spins or inflates as it should.',
      'Honest game wear and nothing worse; ready for another season.',
      'The kind of gear that costs three times this new at the big-box store.',
      'Cleaned up and bundled so you can go straight from checkout to the field.'
    ]
  },
  6: {
    kinds: ['books'],
    band: [6, 28],
    titles: [
      'National Geographic Run - 1970s Decade Box',
      'Vintage Cookbook Lot - 8 pc',
      'Complete Narnia Set - Matched Editions',
      'Field Guide Collection - Birds & Trees',
      'Stephen King Hardcover Lot - 5',
      'Vintage Atlas - 1962 World Edition',
      "Louis L'Amour Paperback Lot - 12",
      'Leather-Bound Classics - 6 Volumes',
      'Childrens Picture Book Bundle - 15',
      'Encyclopedia Set - Complete Run',
      'Poetry Hardcovers - 3 Volumes',
      'Art Monograph Lot - 4 Oversize',
      "Children's Golden Books - 20 pc Lot"
    ],
    details: [
      'Spines are straight and pages clean - shelf wear only where you would expect it.',
      'Sorted and counted twice by our book corner volunteer.',
      'Smells like a good library, not a damp basement - we check.',
      'A ready-made shelf for a reader or a set dresser.'
    ]
  },
  7: {
    kinds: ['lamp', 'mirror', 'teapot'],
    band: [12, 85],
    titles: [
      'Milk Glass Vase Collection - 5 pc',
      'Brass Candlestick Lot - Weighted',
      'Framed Botanical Prints - Set of 4',
      'Stained Glass Window Panel',
      'Macrame Wall Hanging - Large',
      'Ceramic Table Lamp Pair - Rewired',
      'Vintage Quilt - Hand Stitched',
      'Cuckoo Clock - Black Forest Style',
      'Hand-Blown Glass Vase - Signed',
      'Wrought Iron Wall Sconce Pair',
      'Woven Basket Collection - 6 pc',
      'Schoolhouse Wall Clock',
      'Glazed Ceramic Planter Trio',
      'Gilt Frame Mirror - Oval',
      'Pressed Botanical Set - Framed',
      'Copper Weathervane',
      'Batik Wall Textile - Large',
      'Carved Wooden Bookends Pair',
      'Art Pottery Bud Vase Lot - 4',
      'Beveled Glass Terrarium',
      'Folk Art Painted Tray'
    ],
    details: [
      'Dusted, polished and photographed in natural light - no filters needed.',
      'The patina is the honest kind that takes decades, not a spray can.',
      'Hardware is intact and it hangs (or stands) exactly as intended.',
      'From a decorated home where every piece clearly had its spot.'
    ]
  },
  8: {
    kinds: ['records'],
    band: [10, 120],
    titles: [
      'Classic Rock LP Lot - 15 Albums',
      'Motown 45s - Sleeve Lot',
      'Fender Practice Amp - Tested',
      'Ukulele + Gig Bag',
      'Big Band 78s - Crate Lot',
      'Violin - Student Size w/ Case',
      'Jazz Cassette Collection - 30 Tapes',
      'Blues LP Collection - 12 Albums',
      'Harmonica Set - Cased',
      'Bongo Drum Pair - Skin Heads'
    ],
    details: [
      'Play-tested on the shop turntable - surface noise stays behind the music.',
      'Sleeves show shelf wear; the media grades VG or better.',
      'Tuned up and ready to make noise the day you take it home.',
      'From a single-owner collection, which is exactly what you hope to hear.'
    ]
  },
  9: {
    kinds: ['books'],
    band: [8, 60],
    titles: [
      'Lego Bulk Bin - 4 lbs Mixed',
      'Fisher-Price Chatter Phone',
      'Wooden Train Set - 60 pc',
      'Barbie Dreamhouse - 1990s',
      'Hot Wheels Lot - 40 Cars',
      'Radio Flyer Wagon - Steel',
      'Vintage Lincoln Logs - Tin Canister',
      'Puzzle Bundle - 6 Complete',
      'Board Game Lot - 5 Complete',
      'Wooden Building Block Set',
      'Model Train Cars - 8 pc',
      'Stuffed Animal Bundle - Cleaned'
    ],
    details: [
      'Counted piece by piece at the toy table - complete means complete.',
      'Wiped down and checked for the small parts that always go missing.',
      'Played with, clearly loved, and still ready for round two.',
      'The nostalgia hits before you even reach the shelf.'
    ]
  },
  10: {
    kinds: ['watch'],
    band: [18, 160],
    titles: [
      'Timex Marlin - Hand Wound',
      'Sterling Charm Bracelet',
      'Turquoise & Silver Ring Lot',
      'Pocket Watch - Elgin, Running',
      'Coach Leather Watch - Tank Style',
      'Costume Brooch Collection - 12 pc',
      'Casio G-Shock - New Battery',
      'Cameo Pendant - Carved Shell',
      'Pearl Strand - Knotted, 18 inch',
      'Art Deco Cocktail Ring',
      'Pocket Watch - Wind Tested'
    ],
    details: [
      'Keeps time within a minute a day on the wrist - we wore it to check.',
      'Stones are seated and clasps close with a proper click.',
      'Polished gently; the character stays, the grime goes.',
      'Checked against our jewelry references before it earned this tag.'
    ]
  },
  11: {
    kinds: ['dresser'],
    band: [75, 240],
    titles: [
      'Bassett 9-Drawer Dresser - Walnut',
      'Cedar-Lined Wardrobe',
      'Mid-Century Highboy Dresser',
      'Painted Cottage Dresser - 4 Drawer',
      'Industrial Metal Storage Cabinet',
      'Teak Tallboy - Danish Style',
      'Highboy Dresser - Six Drawer',
      'Painted Cottage Chest',
      'Mirrored Vanity with Bench',
      'Steamer Trunk - Restored',
      'Linen Press Cabinet'
    ],
    details: [
      'Drawers glide on their original runners - waxed, not forced.',
      'Solid and sturdy with no wobble; the back panel is intact.',
      'Freshly cleaned and oiled by our furniture team.',
      'Original pulls, which matter more than most people think.'
    ]
  },
  12: {
    kinds: ['chair'],
    band: [40, 190],
    titles: [
      'Mid-Century Rocker - Reupholstered',
      'Cane-Back Armchair',
      'Leather Recliner - Broken In',
      'Windsor Dining Chairs - Set of 4',
      'Velvet Slipper Chair - Emerald',
      'Bentwood Cafe Chair Pair',
      'Wingback Armchair - Reupholstered',
      'Swivel Barstool Pair',
      'Slipper Chair - Velvet',
      'Church Pew Bench - 5 ft',
      'Papasan Chair + Cushion',
      'Folding Director Chairs - Pair'
    ],
    details: [
      'Sat-tested by half the staff - the frame does not creak or shift.',
      'Upholstery is clean with no smoke or pet history we could find.',
      'Legs are level and the joints have been checked and tightened.',
      'The kind of chair that gets claimed the moment it hits a living room.'
    ]
  },
  13: {
    kinds: ['chair'],
    band: [60, 220],
    titles: [
      'Drop-Leaf Maple Table',
      'Round Oak Pedestal Table',
      'Mid-Century Coffee Table - Surfboard',
      'Butcher Block Kitchen Island',
      'Tile-Top Patio Table',
      'Walnut Side Table Pair',
      'Drop-Leaf Dining Table',
      'Nesting Table Trio',
      'Marble-Top Bistro Table',
      'Trestle Farm Table - 6 ft'
    ],
    details: [
      'The top is honest - a ring or two of history, no structural complaints.',
      'Leaves, leverage and hardware all present and working.',
      'Cleaned and conditioned so the grain reads the way it should.',
      'Stands rock solid on a level floor - we checked with a coffee cup.'
    ]
  },
  21: {
    kinds: ['jacket'],
    band: [24, 95],
    titles: [
      "Pendleton Wool Coat - Women's M",
      'Members Only Jacket - Navy, M',
      'Sherpa-Lined Ranch Jacket - L',
      "Harris Tweed Blazer - Men's 40R",
      'Vintage Varsity Jacket - Wool & Leather',
      'Barn Coat - Waxed Canvas, XL',
      'Shearling Aviator Jacket - Large',
      'Quilted Field Vest - Medium',
      "Peacoat - Navy Wool, Men's M"
    ],
    details: [
      'Zips, snaps and buttons all pull their weight - checked twice.',
      'The lining is clean and the cuffs still hold their shape.',
      'Warmth-to-price ratio you simply cannot buy new.',
      'Broken in exactly enough, beat up not at all.'
    ]
  },
  22: {
    kinds: ['jacket'],
    band: [28, 88],
    titles: [
      "Levi's 501 Jeans - 34x32, Faded",
      'Wrangler Cowboy Cut Jeans - 33x30',
      "Levi's Sherpa Trucker - Men's L",
      'Lee 101 Rider Jacket - M',
      'Carhartt Double-Knee Dungarees - 36x30',
      'Vintage Overalls - Hickory Stripe, L'
    ],
    details: [
      'The fade is the real, earned kind - no factory distressing here.',
      'Seams, rivets and belt loops all intact; no blowouts.',
      'Measured flat by our denim wall regulars, so the tag is trustworthy.',
      'Heavyweight denim from when that meant something.'
    ]
  },
  41: {
    kinds: ['camera'],
    band: [35, 160],
    titles: [
      'Pentax K1000 + 50mm - Film Tested',
      'Minolta X-700 Body - Meter Works',
      'Yashica Twin-Lens - Display or Shoot',
      'Olympus OM-1 + Case',
      'Kodak Brownie - Art Deco Faceplate',
      'Nikon FM Body - Chrome',
      'Canonet QL17 Rangefinder'
    ],
    details: [
      'Shutter fires at every speed and the meter reads true against our reference.',
      'We ran a test roll through it and every frame came back.',
      'Glass is clear with no fungus or haze under the loupe.',
      'Light seals look serviceable; shoot it or shelve it with pride.'
    ]
  },
  42: {
    kinds: ['records', 'camera'],
    band: [25, 140],
    titles: [
      'Pioneer Bookshelf Speakers - Pair',
      'Marantz Receiver - Serviced',
      'Sony Discman + Case',
      'Technics Cassette Deck - Tested',
      'AR Turntable - New Belt',
      'Sansui Amplifier - Recapped',
      'Bose 201 Speakers - Tested'
    ],
    details: [
      'Bench-tested at volume - both channels clean, no crackle in the pots.',
      'Fresh belt where it needed one; everything else is original and happy.',
      'Warm sound the new stuff keeps trying to imitate.',
      'Cables and manual in the box where we could find them.'
    ]
  },
  51: {
    kinds: ['hjersey', 'hskates', 'hstick', 'hcards', 'hgear', 'hmask0', 'hmask', 'hrecords'],
    band: [15, 150],
    titles: [
      'CCM Tacks Skates - Size 9',
      'Bauer Vapor Stick - Left, 87 Flex',
      'Goalie Blocker & Trapper Set',
      'Vintage Boston Pennant Lot',
      'Hockey Card Wax Packs - Unopened Lot',
      'Practice Jersey Lot - 3 Sizes',
      'CCM Helmet + Cage - Senior M',
      'Signed Game Program - 1980s',
      'Street Hockey Net + Sticks',
      'Warrior Gloves - 14 inch',
      'Youth Shoulder Pads - Ages 10-12',
      'Beer League Starter Kit - Complete Set',
      'Goalie Pads - 34 inch, Rebuilt Straps',
      'Vintage Wool Team Sweater - L',
      'Rookie Card Lot - Sleeved & Sorted',
      'Skate Sharpening Punch Card + Stone',
      'Bauer Vapor Skates - Senior 9',
      'Composite Stick Lot - 3 Sticks',
      'Goalie Catcher - Intermediate',
      'Elbow Pad Pair - Senior L',
      'Team Jersey Lot - 4 Sizes',
      'Shin Guards - Junior 13 inch',
      'Wheeled Hockey Bag - Senior',
      'Puck Collection - 20 Assorted',
      'Neck Guard + Mouthguard Set',
      'Youth Helmet - Certified, Small',
      'Goalie Stick - Intermediate',
      'Skate Guards + Towel Set',
      'Practice Cone Set - 12',
      'Stick Wax + Tape Bundle',
      'Hockey Pants - Senior Medium',
      'Vintage Team Photo - Framed',
      'Signed Puck - Display Case',
      'Roller Hockey Skates - Size 8',
      'Goalie Mask - Repainted'
    ],
    details: [
      'Checked over on the shop bench - edges true, rivets tight, no hidden cracks.',
      'From a local league cleanout, so expect honest game wear and nothing worse.',
      'Sized and labeled at the gear desk, ready to try on at the counter.',
      'The kind of kit that costs three times this at the pro shop.',
      'Straps, laces and padding all checked before it earned a rack spot.'
    ]
  },
  52: {
    kinds: ['bike'],
    band: [45, 180],
    titles: [
      'Raleigh 3-Speed - Serviced',
      'Peugeot Road Bike - 56cm',
      'BMX Cruiser - Chrome',
      'Burley Kid Trailer',
      'Fuji Touring Frame + Wheels',
      "Schwinn Varsity - Collector's Ride"
    ],
    details: [
      'Tuned in-house: cables, brakes and hubs all checked and adjusted.',
      'Rolls straight and shifts clean on the test loop behind the store.',
      'Some chrome pitting and paint chips; all function, no drama.',
      'Tires hold air and the drivetrain is quieter than it has a right to be.'
    ]
  },
  53: {
    kinds: ['skis', 'hskis'],
    band: [40, 140],
    titles: [
      'Burton Snowboard - 150 cm',
      'K2 Skis + Marker Bindings',
      'Salomon Ski Boots - 27.5',
      'Wood Toboggan - 6 ft',
      'Cross-Country Ski Package',
      'Kids Snowboard + Boots Bundle',
      'Snowshoe Pair + Poles',
      'Ski Helmet - Adult Medium',
      'Toboggan Lot - 3 Sleds'
    ],
    details: [
      'Edges sharpened and bases freshly waxed by our winter bench.',
      'Bindings checked and torqued; bring your boots for a fit check.',
      'A few filled scratches in the base - character, not damage.',
      'Ready for the hill the same day you pick it up.'
    ]
  }
};

/** Generated rows per company - weighted by roster size and specialty. */
const TENANT_PLANS: { tenantId: number; mix: [categoryId: number, count: number][] }[] = [
  // Habitat ReStore LA (4 stores) - salvage / furniture heavy.
  {
    tenantId: 2,
    mix: [
      [1, 7],
      [11, 6],
      [12, 6],
      [13, 6],
      [7, 8],
      [3, 3],
      [6, 1],
      [2, 1]
    ]
  },
  // Matt's Finds (5 stores) - the largest catalog, hockey heavy.
  {
    tenantId: 4,
    mix: [
      [51, 34],
      [53, 5],
      [52, 3],
      [5, 4],
      [9, 3],
      [6, 2],
      [8, 2],
      [10, 3]
    ]
  },
  // Goodwill Industries of New Mexico (20 stores) - the widest roster and the
  // marketplace's general-thrift anchor, so the mix is both the deepest and the
  // broadest: it stocks every store on the first pass and still carries the
  // everyday categories the specialist retailers do not.
  {
    tenantId: 7,
    mix: [
      [2, 12],
      [21, 6],
      [22, 6],
      [3, 12],
      [4, 6],
      [41, 5],
      [42, 5],
      [6, 9],
      [7, 12],
      [8, 7],
      [9, 8],
      [10, 7],
      [1, 5],
      [11, 4],
      [12, 5],
      [13, 3],
      [5, 5],
      [52, 2],
      [53, 2]
    ]
  }
];

const BULKY_CATEGORIES = new Set([1, 11, 12, 13, 52]);
const PARCEL_SHIP_PRICES = [5.99, 6.99, 7.99, 8.99, 9.99, 11.99, 12.99, 14.99, 16.99, 19.99];
const FREIGHT_SHIP_PRICES = [69, 79, 89, 99];

export interface GeneratedAuctionSpec {
  startPrice: number;
  currentBid: number;
  /** Per-bid step used to derive currentBid and the bid history, NOT the
   *  auction increment - that comes from the ADM-02 band table. */
  bidStep: number;
  bidCount: number;
  bidderCount: number;
  reservePrice: number | null;
  reserveMet: boolean;
  buyNowPrice: number | null;
  /** Clock offsets - resolved to Dates at seed time (staggered minutes to days). */
  endsInMinutes: number;
  startedDaysAgo: number;
}

export interface GeneratedListingSpec {
  id: number;
  title: string;
  kind: string;
  photoParams: string;
  condition: ItemCondition;
  conditionNote: string | null;
  categoryId: number;
  storeId: number;
  description: string;
  /** Buy Now price; null for auction rows. */
  price: number | null;
  /** Shipping price; null = pickup only. */
  ship: number | null;
  freight: boolean;
  auction: GeneratedAuctionSpec | null;
}

/** Auction economics derived from the category band - always self-consistent. */
const buildAuction = (rng: () => number, value: number): GeneratedAuctionSpec => {
  const startPrice = Math.max(5, Math.round(value * 0.3));
  const bidStep = value < 50 ? 1 : value < 120 ? 3 : value < 250 ? 5 : 10;
  const bidCount = 2 + Math.floor(rng() * 12);
  const currentBid = startPrice + bidCount * bidStep;
  const reservePrice = rng() < 0.5 ? Math.round(value * 0.85) : null;
  const buyNowPrice = rng() < 0.2 ? Math.round(value * 1.6) : null;
  return {
    startPrice,
    currentBid,
    bidStep,
    bidCount,
    bidderCount: Math.max(2, Math.ceil(bidCount * 0.6)),
    reservePrice,
    reserveMet: reservePrice !== null && currentBid >= reservePrice,
    buyNowPrice,
    endsInMinutes: 30 + Math.floor(rng() * 10050),
    startedDaysAgo: 2 + Math.floor(rng() * 5)
  };
};

/** Flagship-weighted store pick - index 0 weight 3, index 1 weight 2, rest 1. */
const weightedStoreIndex = (rng: () => number, size: number): number => {
  const total = size + 3;
  let roll = rng() * total;
  for (let index = 0; index < size; index++) {
    roll -= index === 0 ? 3 : index === 1 ? 2 : 1;
    if (roll < 0) return index;
  }
  return size - 1;
};

/**
 * The pure, fully deterministic generator output (no Dates) - exported so the
 * integrity spec can assert two runs are identical and so `auction.seed.ts`
 * can derive matching auction rows.
 */
export const generatedListingSpecs = (): GeneratedListingSpec[] => {
  const rng = mulberry32(GENERATOR_SEED);
  const kindUses = new Map<string, number>();
  const titleCursors = new Map<number, number>();
  const companies = seedCompanies();
  const specs: GeneratedListingSpec[] = [];
  let id = 200;

  for (const plan of TENANT_PLANS) {
    const roster = companies.find(company => company.id === plan.tenantId)!.stores.map(store => store.id);
    const categoryIds = plan.mix.flatMap(([categoryId, count]) => Array.from({ length: count }, () => categoryId));

    categoryIds.forEach((categoryId, index) => {
      const pool = CATEGORY_POOLS[categoryId];
      // Every store gets inventory: the first pass walks the roster in order,
      // then flagship-weighted picks fill the rest.
      const storeId = index < roster.length ? roster[index] : roster[weightedStoreIndex(rng, roster.length)];

      const titleCursor = titleCursors.get(categoryId) ?? 0;
      titleCursors.set(categoryId, titleCursor + 1);
      const title = pool.titles[titleCursor % pool.titles.length];

      const kind = pick(rng, pool.kinds);
      const kindUse = kindUses.get(kind) ?? 0;
      kindUses.set(kind, kindUse + 1);

      const conditionRoll = rng();
      const condition =
        conditionRoll < 0.45
          ? ItemCondition.Good
          : conditionRoll < 0.79
            ? ItemCondition.Excellent
            : conditionRoll < 0.92
              ? ItemCondition.Fair
              : ItemCondition.NewWithTags;
      const conditionNote =
        condition === ItemCondition.Fair
          ? pick(rng, FAIR_NOTES)
          : condition === ItemCondition.Good && rng() < 0.3
            ? pick(rng, GOOD_NOTES)
            : null;

      const [low, high] = pool.band;
      const value = low + Math.round(rng() * (high - low));

      const bulky = BULKY_CATEGORIES.has(categoryId);
      const pickupOnly = rng() < (bulky ? 0.45 : 0.15);
      const freight = bulky && !pickupOnly;
      const ship = pickupOnly ? null : freight ? pick(rng, FREIGHT_SHIP_PRICES) : pick(rng, PARCEL_SHIP_PRICES);

      const auction = rng() < 0.15 ? buildAuction(rng, value) : null;

      const sentences = [pick(rng, OPENERS), pick(rng, pool.details)];
      if (rng() < 0.6) sentences.push(pick(rng, CLOSERS));

      specs.push({
        id: id++,
        title,
        kind,
        photoParams: photoVariant(kindUse),
        condition,
        conditionNote,
        categoryId,
        storeId,
        description: sentences.join(' '),
        price: auction ? null : value,
        ship,
        freight,
        auction
      });
    });
  }

  return specs;
};

/**
 * New Mexico showcase rows (ids 500+). Goodwill NM is a real pilot retailer,
 * so its catalog carries what its stores actually process: turquoise and
 * silver, Pendleton wool, pueblo pottery, Route 66 pieces and Southwest
 * titles. These rows also back the keyword suggestions on the ThriftSpotters
 * sign-up, so a shopper who taps "Turquoise" or "Pendleton" finds real stock
 * instead of an empty result.
 *
 * Placement is deliberate, not decorative: the generator leaves the smaller
 * stores thin, and five of them (Coors, Menaul, Rio Rancho, Los Lunas,
 * Carlsbad) drew no auction at all, so each gets one here. Auction rows
 * 1101-1105 in `auction.seed.ts` mirror these exactly.
 */
const newMexicoListings = (): ListingDetailState[] => [
  make({
    id: 500,
    title: 'Squash Blossom Necklace - Sterling & Turquoise',
    kind: 'hwatch',
    auction: { currentBid: 210, bidCount: 14, endsAt: hoursFromNow(4), reserveMet: true, buyNowPrice: 420 },
    condition: ItemCondition.Excellent,
    categoryId: 10,
    storeId: 74,
    ship: 12.99,
    description:
      'A classic squash blossom in sterling with graduated turquoise stones and a hand-stamped naja. Weighed ' +
      'and tested at the jewelry bench, stones tight in their bezels, clasp strong. The kind of piece that ' +
      'usually leaves with a collector the day it hits the case.',
    honestFlaws: 'Light surface scratching on the naja and expected patina in the stamp work.',
    materials: 'Sterling silver, natural turquoise',
    era: 'Mid-century Southwest'
  }),
  make({
    id: 501,
    title: 'Navajo-Style Wool Rug - 4 x 6 ft',
    kind: 'jacket',
    auction: { currentBid: 128, bidCount: 9, endsAt: hoursFromNow(8) },
    condition: ItemCondition.Good,
    conditionNote: 'even wear, no holes',
    categoryId: 7,
    storeId: 77,
    ship: 24.99,
    description:
      'Handwoven wool rug in a banded Ganado palette of red, charcoal and cream. Cleaned and inspected on the ' +
      'textile table; pile is even and the selvedge is intact all the way round.',
    honestFlaws: 'Slight fading along one edge from sun exposure, and minor fringe loss at two corners.',
    dimensionsLabel: '48″ W × 72″ L',
    materials: 'Wool'
  }),
  make({
    id: 502,
    title: 'Acoma-Style Pottery Olla - Hand Coiled',
    kind: 'teapot',
    auction: { currentBid: 74, bidCount: 7, endsAt: hoursFromNow(30) },
    condition: ItemCondition.Excellent,
    categoryId: 7,
    storeId: 83,
    ship: 18.99,
    description:
      'Hand-coiled olla with fine-line geometric banding in black on white slip. Rings true when tapped, with ' +
      'no repair and no restoration. Signed on the underside.',
    materials: 'Coiled and slipped earthenware',
    era: 'Late 20th century'
  }),
  make({
    id: 503,
    title: 'Pendleton Wool Blanket - Chief Joseph Pattern',
    kind: 'jacket',
    auction: { currentBid: 96, bidCount: 11, endsAt: hoursFromNow(19), reserveMet: true },
    condition: ItemCondition.Excellent,
    categoryId: 7,
    storeId: 85,
    ship: 16.99,
    description:
      'Genuine Pendleton in the Chief Joseph pattern, wool label intact. Laundered by our textile team and ' +
      'still full-bodied with no thinning. Folds to the size of a couch throw and outlasts anything new at ' +
      'the price.',
    dimensionsLabel: '64″ W × 80″ L',
    materials: 'Pure virgin wool'
  }),
  make({
    id: 504,
    title: 'Turquoise Cuff Bracelet - Kingman Stone',
    kind: 'hwatch',
    auction: { currentBid: 58, bidCount: 6, endsAt: hoursFromNow(43) },
    condition: ItemCondition.Good,
    conditionNote: 'honest wear',
    categoryId: 10,
    storeId: 90,
    ship: 9.99,
    description:
      'Heavy sterling cuff set with a single Kingman stone and twisted-wire surround. Bench tested, stone ' +
      'seated firm, cuff adjusts without complaint.',
    honestFlaws: 'Tarnish in the recessed stamp work and one small chip at the stone edge.',
    materials: 'Sterling silver, Kingman turquoise'
  }),
  make({
    id: 505,
    title: 'Zuni Inlay Ring - Size 8',
    kind: 'hwatch',
    condition: ItemCondition.Excellent,
    categoryId: 10,
    storeId: 87,
    price: 68,
    ship: 6.99,
    description:
      'Channel inlay ring in turquoise, coral and jet on a sterling shank. All stones present and flush, no ' +
      'lifting at the channels.',
    materials: 'Sterling silver, turquoise, coral, jet'
  }),
  make({
    id: 506,
    title: 'Sterling & Turquoise Bolo Tie',
    kind: 'hwatch',
    condition: ItemCondition.Good,
    categoryId: 10,
    storeId: 87,
    price: 84,
    ship: 7.99,
    description:
      'Stamped sterling slide with a domed turquoise cabochon, on a braided leather cord with silver tips. ' +
      'Cord is supple and the slide grips properly.',
    honestFlaws: 'Cord shows wear near the tips; the slide has light scratching on the back.',
    materials: 'Sterling silver, turquoise, leather'
  }),
  make({
    id: 507,
    title: "Pendleton Wool Coat - Women's M",
    kind: 'jacket',
    condition: ItemCondition.Excellent,
    categoryId: 21,
    storeId: 84,
    price: 128,
    ship: 14.99,
    description:
      'Pendleton wool coat in a muted Southwest stripe, fully lined, with every button original. Checked at ' +
      'the seams and pressed before it earned a rack spot.',
    materials: 'Wool shell, satin lining'
  }),
  make({
    id: 508,
    title: 'Santa Clara Blackware Bowl - Signed',
    kind: 'teapot',
    condition: ItemCondition.Excellent,
    categoryId: 7,
    storeId: 84,
    price: 145,
    ship: 16.99,
    description:
      'Polished blackware bowl with a carved avanyu band, signed on the base. Deep even burnish with no ' +
      'crazing and no repair.',
    materials: 'Burnished blackware',
    era: 'Late 20th century'
  }),
  make({
    id: 509,
    title: 'Fiestaware Dinner Set - 12 Piece',
    kind: 'teapot',
    condition: ItemCondition.Good,
    conditionNote: 'light utensil marks',
    categoryId: 3,
    storeId: 84,
    price: 92,
    ship: 19.99,
    description:
      'Twelve pieces of genuine Fiesta in turquoise, scarlet and sunflower: four dinner plates, four salad ' +
      'plates and four bowls. All marked, all dishwasher safe, no chips.',
    honestFlaws: 'Utensil marks on two dinner plates, visible only at an angle.',
    materials: 'Glazed ceramic'
  }),
  make({
    id: 510,
    title: 'Vintage Pyrex Butterprint Bowl Set',
    kind: 'teapot',
    condition: ItemCondition.Good,
    categoryId: 3,
    storeId: 79,
    price: 78,
    ship: 12.99,
    description:
      'Three nesting Pyrex bowls in the Butterprint pattern, the amish-couple print collectors hunt for. ' +
      'Print is crisp with no dishwasher fade on the two larger bowls.',
    honestFlaws: 'The smallest bowl has some print softening and a faint scratch inside.',
    era: '1950s-1960s'
  }),
  make({
    id: 511,
    title: 'Lodge Cast Iron Dutch Oven - 6 qt',
    kind: 'mixer',
    condition: ItemCondition.Good,
    conditionNote: 're-seasoned in house',
    categoryId: 3,
    storeId: 88,
    price: 42,
    ship: 14.99,
    description:
      'Six-quart Lodge dutch oven with the lid, stripped and re-seasoned on our bench. Cooking surface is ' +
      'smooth and slick, sits flat, no wobble and no cracks.',
    materials: 'Cast iron'
  }),
  make({
    id: 512,
    title: 'Route 66 Painted Metal Sign',
    kind: 'mirror',
    condition: ItemCondition.Fair,
    conditionNote: 'weathered, as found',
    categoryId: 7,
    storeId: 88,
    price: 36,
    ship: 11.99,
    description:
      'Painted steel Route 66 shield with genuine roadside weathering. Not a reproduction finish, this one ' +
      'earned its rust outdoors.',
    honestFlaws: 'Surface rust across the lower third and two mounting holes elongated from use.',
    materials: 'Painted steel'
  }),
  make({
    id: 513,
    title: 'New Mexico License Plate Lot - 6 Plates, 1950s to 1970s',
    kind: 'mirror',
    condition: ItemCondition.Fair,
    categoryId: 7,
    storeId: 88,
    price: 54,
    ship: 9.99,
    description:
      'Six New Mexico plates spanning three decades, including two yellow-on-black issues. Sorted, flattened ' +
      'where safe to do so, and priced as a lot.',
    honestFlaws: 'All show rust, bends and paint loss consistent with age. Two have bolt-hole tears.',
    era: '1950s-1970s'
  }),
  make({
    id: 514,
    title: 'Zia Sun Framed Print - Signed',
    kind: 'mirror',
    condition: ItemCondition.Excellent,
    categoryId: 7,
    storeId: 81,
    price: 44,
    ship: 11.99,
    description:
      'Signed and numbered Zia sun print in a simple oak frame, glass clean and intact, wire hanger already ' +
      'fitted. Ready to hang the day you collect it.',
    dimensionsLabel: '16″ W × 20″ H',
    materials: 'Paper print, oak frame'
  }),
  make({
    id: 515,
    title: 'Mid-Century Teak Credenza',
    kind: 'dresser',
    condition: ItemCondition.Good,
    conditionNote: 'refinished top',
    categoryId: 11,
    storeId: 81,
    price: 340,
    ship: 89,
    freight: true,
    shipping: { weightLb: 88, lengthIn: 60, widthIn: 18, heightIn: 30 },
    retailEstimate: 900,
    description:
      'Teak credenza with tambour doors and the original tapered legs. Doors track smoothly after a clean and ' +
      'wax, interior shelving intact, carcass solid with no racking.',
    honestFlaws: 'The top was refinished before donation, so the sheen differs slightly from the sides.',
    dimensionsLabel: '60″ W × 18″ D × 30″ H',
    materials: 'Teak veneer, solid teak legs',
    era: '1960s · Mid-Century Modern'
  }),
  make({
    id: 516,
    title: 'Brass Floor Lamp - Adjustable',
    kind: 'lamp',
    condition: ItemCondition.Good,
    categoryId: 7,
    storeId: 81,
    price: 58,
    ship: 16.99,
    description:
      'Adjustable brass floor lamp, rewired and PAT checked on our bench with a new cord and plug. Shade is ' +
      'clean and the joint holds position without drifting.',
    honestFlaws: 'Patina and small dents on the base, polished back but still visible.',
    materials: 'Brass, linen shade'
  }),
  make({
    id: 517,
    title: 'Tony Hillerman - Dance Hall of the Dead, First Edition',
    kind: 'books',
    condition: ItemCondition.Good,
    categoryId: 6,
    storeId: 86,
    price: 85,
    ship: 5.99,
    description:
      "First edition of Hillerman's second Leaphorn novel, the one that won the Edgar. Binding tight, text " +
      'block clean and square, jacket present in a protective sleeve.',
    honestFlaws:
      'Jacket has edge wear and a closed tear at the top of the spine panel. Previous owner name ' +
      'on the front endpaper.',
    era: '1973'
  }),
  make({
    id: 518,
    title: 'Southwest History Book Lot - 7 Volumes',
    kind: 'books',
    condition: ItemCondition.Good,
    categoryId: 6,
    storeId: 86,
    price: 32,
    ship: 9.99,
    description:
      'Seven volumes on New Mexico and borderlands history, including two university press titles and a ' +
      'photographic survey of the pueblos. All complete, no water damage.',
    honestFlaws: 'Mixed shelf wear; one volume has underlining in the first chapter.'
  }),
  make({
    id: 519,
    title: 'Saltillo Serape Blanket - Handwoven',
    kind: 'jacket',
    condition: ItemCondition.Good,
    categoryId: 7,
    storeId: 90,
    price: 76,
    ship: 12.99,
    description:
      'Handwoven serape with a classic centre diamond and fine warp count. Colours are still saturated and ' +
      'the weave is tight end to end.',
    honestFlaws: 'Two small repairs on the reverse, neatly done, and light fringe loss.',
    dimensionsLabel: '46″ W × 78″ L',
    materials: 'Wool'
  }),
  make({
    id: 520,
    title: "Cowboy Boots - Tony Lama, Men's 10D",
    kind: 'jacket',
    condition: ItemCondition.Good,
    conditionNote: 'resoled',
    categoryId: 2,
    storeId: 89,
    price: 88,
    ship: 13.99,
    description:
      'Tony Lama boots in tooled brown leather, professionally resoled before donation. Uppers are supple, ' +
      'stitching intact, heels square and even.',
    honestFlaws: 'Creasing across both vamps and scuffing at the toes.',
    materials: 'Leather'
  }),
  make({
    id: 521,
    title: 'Concert Tee - 1990s Tour, Size L',
    kind: 'jacket',
    condition: ItemCondition.Good,
    conditionNote: 'soft, well worn',
    categoryId: 2,
    storeId: 71,
    price: 46,
    ship: 6.99,
    description:
      'Single-stitch tour tee with the dates on the reverse, worn to exactly the softness people pay for. ' +
      'Graphic is cracked in the way collectors want, not flaking off.',
    honestFlaws: 'Small hole near the left hem and a faded collar.',
    era: '1990s'
  }),
  make({
    id: 522,
    title: "Levi's 501 Denim - 34x32, Made in USA",
    kind: 'jacket',
    condition: ItemCondition.Good,
    categoryId: 22,
    storeId: 71,
    price: 58,
    ship: 7.99,
    description:
      'Made-in-USA 501s with a red tab and button-fly, honest fades at the seat and knees. Measured flat at ' +
      'the counter so the size on the tag is the size you get.',
    honestFlaws: 'Light wear at the hem and one small repair inside the back pocket.',
    materials: 'Cotton denim'
  }),
  make({
    id: 523,
    title: 'Kids Clothing Lot - Size 4T, 12 Pieces',
    kind: 'jacket',
    condition: ItemCondition.Good,
    categoryId: 2,
    storeId: 85,
    price: 28,
    ship: 9.99,
    description:
      'Twelve pieces in 4T: five tops, four bottoms, two sweaters and a light jacket. Sorted, laundered and ' +
      'checked for stains before bagging.',
    honestFlaws: 'Two tops have faint marks at the cuffs. Priced for it.'
  }),
  make({
    id: 524,
    title: 'LEGO Classic Brick Box - Sorted & Counted',
    kind: 'books',
    condition: ItemCondition.Good,
    categoryId: 9,
    storeId: 83,
    price: 34,
    ship: 11.99,
    description:
      'A full brick box sorted by colour and weighed at the counter, with the baseplate and two minifigures ' +
      'included. Washed before it went on the shelf.',
    honestFlaws: 'Mixed sets rather than one complete kit, and the box itself is a replacement.'
  }),
  make({
    id: 525,
    title: 'Jigsaw Puzzle Lot - 5 Puzzles, All Pieces Counted',
    kind: 'books',
    condition: ItemCondition.Good,
    categoryId: 9,
    storeId: 83,
    price: 18,
    ship: 10.99,
    description:
      'Five puzzles between 500 and 1000 pieces, every one counted at the sorting table and bagged. Two are ' +
      'Southwest landscape scenes.',
    honestFlaws: 'Boxes show shelf wear; one lid is taped at a corner.'
  }),
  make({
    id: 526,
    title: 'Soccer Cleats - Adidas, Youth 5',
    kind: 'hgear',
    condition: ItemCondition.Good,
    categoryId: 5,
    storeId: 80,
    price: 22,
    ship: 8.99,
    description:
      'Youth cleats with plenty of season left, studs checked and even, laces replaced. Cleaned and deodorized ' +
      'at the gear desk.',
    honestFlaws: 'Scuffing across both toe boxes.'
  }),
  make({
    id: 527,
    title: 'Mountain Bike - Trek 820, Serviced',
    kind: 'bike',
    condition: ItemCondition.Good,
    conditionNote: 'tuned in house',
    categoryId: 52,
    storeId: 80,
    price: 165,
    description:
      'Trek 820 hardtail tuned on our bike bench: cables, brakes and hubs adjusted, tyres hold air and the ' +
      'drivetrain shifts clean on the test loop. Pickup only so we can size it to you at the counter.',
    honestFlaws: 'Paint chips on the top tube and light rust on the chainring bolts.',
    materials: 'Steel frame'
  }),
  make({
    id: 528,
    title: 'Cross-Country Ski Package',
    kind: 'skis',
    condition: ItemCondition.Good,
    categoryId: 53,
    storeId: 86,
    price: 96,
    ship: 29.99,
    description:
      'Skis, poles and boots as a package, bases freshly waxed by our winter bench and bindings checked and ' +
      'torqued. Bring your socks for a fit check at the counter.',
    honestFlaws: 'Filled base scratches on both skis and scuffing on the boot cuffs.'
  }),
  make({
    id: 529,
    title: 'Canon AE-1 Program + 50mm Lens',
    kind: 'camera',
    condition: ItemCondition.Good,
    conditionNote: 'film tested',
    categoryId: 41,
    storeId: 74,
    price: 158,
    ship: 12.99,
    description:
      'AE-1 Program with the 50mm f/1.8, run through a test roll before listing. Shutter fires accurately at ' +
      'every speed, meter tracks, light seals replaced.',
    honestFlaws: 'Brassing on the base plate and a small mark on the front element that does not show in ' + 'images.',
    era: '1980s'
  }),
  make({
    id: 530,
    title: 'Technics SL-B2 Turntable - Serviced',
    kind: 'records',
    condition: ItemCondition.Good,
    categoryId: 42,
    storeId: 74,
    price: 145,
    ship: 19.99,
    description:
      'Belt-drive Technics with a new belt and a fresh stylus, speed checked against a strobe. Dust cover ' +
      'present, hinges tight, tonearm tracks without skipping.',
    honestFlaws: 'Dust cover has fine scratches and the plinth has one small chip at the rear corner.'
  }),
  make({
    id: 531,
    title: 'Vinyl Lot - Southwest & Folk, 18 Records',
    kind: 'hrecords',
    condition: ItemCondition.Good,
    categoryId: 8,
    storeId: 77,
    price: 62,
    ship: 14.99,
    description:
      'Eighteen records leaning Southwest, folk and country, each sleeved and play-graded at the counter. No ' +
      'warps in the lot.',
    honestFlaws: 'Jacket ring wear across most of the lot and two split inner sleeves.'
  }),
  make({
    id: 532,
    title: 'Fleetwood Mac Rumours - First Pressing',
    kind: 'records',
    condition: ItemCondition.Good,
    categoryId: 8,
    storeId: 77,
    price: 48,
    ship: 6.99,
    description:
      'First pressing with the correct matrix runout, sleeved and play-graded end to end. Surface is quiet ' +
      'through both sides.',
    honestFlaws: 'Ring wear on the jacket and a name written on the inner sleeve.',
    era: '1977'
  }),
  make({
    id: 533,
    title: 'Hand-Thrown Stoneware Mug Set - 4 Mugs',
    kind: 'teapot',
    condition: ItemCondition.Excellent,
    categoryId: 3,
    storeId: 82,
    price: 26,
    ship: 9.99,
    description:
      'Four hand-thrown stoneware mugs in a speckled oatmeal glaze, all by the same hand and stamped on the ' +
      'foot. No crazing, no chips, dishwasher safe.',
    materials: 'Stoneware'
  }),
  make({
    id: 534,
    title: 'Turquoise Drop Earrings - Sterling',
    kind: 'hwatch',
    condition: ItemCondition.Excellent,
    categoryId: 10,
    storeId: 72,
    price: 38,
    ship: 5.99,
    description:
      'Sterling drops with matched turquoise cabochons and new backs fitted at the jewelry bench. Stones are ' +
      'well matched in tone.',
    materials: 'Sterling silver, turquoise'
  })
];

/**
 * Store-coverage auctions (ids 540+). The generator is random within a
 * tenant's mix, so it can leave a store with no auction at all, which makes
 * that store page look half-built. These two cover the Habitat ReStores it
 * missed; auction rows 1106-1107 mirror them.
 */
const storeCoverageAuctions = (): ListingDetailState[] => [
  make({
    id: 540,
    title: 'Antique Oak Pocket Doors - Matched Pair',
    kind: 'dresser',
    auction: { currentBid: 185, bidCount: 8, endsAt: hoursFromNow(26), reserveMet: true },
    condition: ItemCondition.Good,
    conditionNote: 'original hardware',
    categoryId: 1,
    storeId: 23,
    ship: 99,
    freight: true,
    shipping: { weightLb: 140, lengthIn: 84, widthIn: 36, heightIn: 8 },
    description:
      'A matched pair of quartersawn oak pocket doors salvaged from a 1910 bungalow, with the original mortise ' +
      'hardware still fitted. Stiles and rails are square and the panels are free of splits.',
    honestFlaws: 'Old paint remains in the mouldings and both doors need new rollers, which we can point you to.',
    dimensionsLabel: '84″ H × 36″ W each',
    materials: 'Quartersawn oak',
    era: 'c. 1910'
  }),
  make({
    id: 541,
    title: 'Stained Glass Transom Window - Leaded',
    kind: 'mirror',
    auction: { currentBid: 120, bidCount: 6, endsAt: hoursFromNow(34) },
    condition: ItemCondition.Good,
    categoryId: 7,
    storeId: 24,
    ship: 44,
    description:
      'Leaded transom in ambers and greens, salvaged intact during a Van Nuys remodel. Came lights are sound ' +
      'and the panel sits flat with no bowing.',
    honestFlaws: 'Two small edge chips in the border glass and old putty residue along the frame.',
    dimensionsLabel: '36″ W × 18″ H',
    materials: 'Leaded stained glass, fir frame',
    era: 'Early 20th century'
  })
];

/** Specs → detail rows through the same `make()` pipeline as the hand-crafted seeds. */
const generatedListings = (): ListingDetailState[] =>
  generatedListingSpecs().map(spec =>
    make({
      id: spec.id,
      title: spec.title,
      kind: spec.kind,
      condition: spec.condition,
      conditionNote: spec.conditionNote ?? undefined,
      categoryId: spec.categoryId,
      storeId: spec.storeId,
      description: spec.description,
      price: spec.price ?? undefined,
      ship: spec.ship ?? undefined,
      freight: spec.freight || undefined,
      photoUrls: [photo(spec.kind, spec.photoParams)],
      auction: spec.auction
        ? {
            currentBid: spec.auction.currentBid,
            bidCount: spec.auction.bidCount,
            endsAt: minutesFromNow(spec.auction.endsInMinutes),
            reserveMet: spec.auction.reserveMet,
            buyNowPrice: spec.auction.buyNowPrice ?? undefined
          }
        : undefined
    })
  );

export const seedListings = (): ListingDetailState[] => [
  ...handcraftedListings(),
  ...newMexicoListings(),
  ...storeCoverageAuctions(),
  ...generatedListings()
];
